/** A passed, Job-less attendance says how late it is; a late Job says Job overdue; a paused plan's shows Paused. */
import { VisitAgeState, VisitBrowseRow } from '@jms/model';
import { DateTime } from 'luxon';
import { defineConditionalTag, type ConditionalTag } from '../../shared/define.conditional.tag';
import type { StatusTone } from '../../shared/status.tag.tones';
import { visitPausedTag } from './visit.paused.tag';

type AgedState = Exclude<VisitAgeState, VisitAgeState.Current>;

const TONES: Record<AgedState, StatusTone> = {
  [VisitAgeState.Overdue]: 'warning',
  [VisitAgeState.Missed]: 'error',
};

const JOB_OVERDUE: ConditionalTag = { label: 'Job overdue', tone: 'warning', tooltip: 'The Job is not completed and its appointment has passed' };

export const visitAgeTag = defineConditionalTag<VisitBrowseRow>(row => {

  // Paused takes the age slot so the board's Overdue column and both headers explain why nothing is late.
  const paused = visitPausedTag(row);
  if (paused) return paused;
  // A Job'd Visit never ages, so its slot is free for the Job's own lateness (`visitJobOverdue`, set by the reader).
  if (row?.job?._id) return row.jobOverdue ? JOB_OVERDUE : null;
  const state = row?.ageState;
  // A Job owns the appointment once it exists, so a stale age from an earlier read must not linger.
  if (state !== VisitAgeState.Overdue && state !== VisitAgeState.Missed) return null;
  const tooltip = missedOnTooltip(state, row.missedOn);
  return { label: `${state} · ${ageLabel(row.ageDays ?? 0)}`, tone: TONES[state], ...(tooltip ? { tooltip } : {}) };
});

function ageLabel(days: number): string {

  if (days <= 0) return 'today';
  return days === 1 ? '1 day' : `${days} days`;
}

function missedOnTooltip(state: AgedState, missedOn: string | undefined): string | null {

  const date = DateTime.fromISO(missedOn ?? '');
  if (!date.isValid) return null;
  return state === VisitAgeState.Overdue ? `Becomes missed on ${date.toFormat('d LLL yyyy')}` : `Missed since ${date.toFormat('d LLL yyyy')}`;
}
